import Button from '@material-ui/core/Button';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { Alert, AlertTitle } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { debounce } from 'debounce';
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import LoadingButton from '../common/LoadingButton';
import FamilyWorkflowPermissionsEditor from './FamilyWorkflowPermissionsEditor';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginBottom: 24,
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
        justifyContent: 'space-between'
      }
    },
    title: {
      marginBottom: 24,
      [theme.breakpoints.up('sm')]: {
        marginBottom: 0
      }
    },
    cancelButton: {
      marginRight: 16
    },
    alert: {
      marginBottom: 24
    },
    panel: {
      padding: 24,
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
    },
    section: {
      marginBottom: 24
    },
    sectionHeader: {
      marginBottom: 8,
    },
    largeGutter: {
      marginBottom: 16
    }
  })
);

interface Props {
  family: any;
  title: string;
  groups: any;
  onFamilyChange: (family: any) => void;
  onSave: () => void;
  onSaveComplete: (family: any) => void;
  onCancel: () => void;
}

function FamilySettingsForm(props: Props & RouteComponentProps) {
  const { family, title, groups, onFamilyChange, onSave, onSaveComplete, onCancel } = props;
  const classes = useStyles({});
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [showErrors, setShowErrors] = useState(false);


  const debouncedOnFamilyChange = debounce(onFamilyChange, 200);

  const onTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedOnFamilyChange({ ...family, [event.target.name]: event.target.value });
  };

  const onFamilyAdminsChange = (event: React.ChangeEvent<{}>, value: any) => {
    onFamilyChange({ ...family, Groups: { FamilyAdmins: value } });
  };

  const onAllowedArnsChange = (arns: string[]) => {
    onFamilyChange({ ...family, AllowedARNs: arns });
  };

  const onSaveWrapper = (event) => {
    setValidationError(null);

    if (!family.Name) {
      setShowErrors(true);
    } else {
      setSaveInProgress(true);
      // Check for validation errors
      onSave().then((savedFamily) => {
        setSaveInProgress(false);
        onSaveComplete(savedFamily);
      }).catch(error => {
        setSaveInProgress(false);
        setShowErrors(true);
        console.log(error);
        if (error.status === 400) {
          setValidationError(error.data.errors ? error.data.errors : [error.data.errorMessage]);
        } else if (error.status === 403 || error.status === 409) {
          setValidationError([error.data.errorMessage]);
        } else {
          setValidationError(['Oops, something went wrong... please try again']);
        }
      });
    }
  };

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="h6" gutterBottom className={classes.title}>{title}</Typography>
        <div>
          <Button color="primary" variant="outlined" onClick={onCancel} className={classes.cancelButton}>Cancel</Button>
          <LoadingButton
            color="primary"
            loading={saveInProgress}
            variant="contained"
            onClick={onSaveWrapper}>
            Save
          </LoadingButton>
        </div>
      </div>
      {validationError && <Alert severity="warning" className={classes.alert}>
        <AlertTitle>Failed to save family</AlertTitle>
        {validationError.map((error, index) => <Typography key={index}>{error}</Typography>)}
      </Alert>}
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Family Details</Typography>
        </div>
        <div className={classes.panel}>
          <TextField
            required
            name='Name'
            label='Name'
            className={classes.largeGutter}
            error={showErrors && !family.Name}
            helperText={showErrors && !family.Name ? 'Name is required' : ''}
            defaultValue={family.Name}
            onChange={onTextFieldChange}
            fullWidth
            placeholder='Family Name'
            InputLabelProps={{
              shrink: true
            }} />
          <TextField
            required
            name='Description'
            label='Description'
            defaultValue={family.Description}
            onChange={onTextFieldChange}
            fullWidth
            multiline
            placeholder='Family Description'
            InputLabelProps={{
              shrink: true
            }} />
        </div>
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Family Admins</Typography>
        </div>
        <div className={classes.panel}>
          <Typography color="textSecondary" className={classes.largeGutter}>
            Only the family creator and members of the groups selected below can edit this family.
          </Typography>
          <Autocomplete
            value={family.Groups.FamilyAdmins}
            onChange={onFamilyAdminsChange}
            multiple
            options={Object.keys(groups)}
            getOptionLabel={(option) => groups[option]}
            renderInput={(params) => <TextField {...params} label='Groups' placeholder='Select a group to add' InputLabelProps={{
              shrink: true
            }} />}
          />
        </div>
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Workflow Access Permissions</Typography>
        </div>
        <div className={classes.panel}>
          <Typography color="textSecondary" className={classes.largeGutter}>
            The following IAM ARNs can edit and execute any workflow in this family.
          </Typography>
          <FamilyWorkflowPermissionsEditor allowedArns={family.AllowedARNs} onAllowedArnsChange={onAllowedArnsChange} />
        </div>
      </div>
    </div>
  );
};

export default FamilySettingsForm;
